import Dexie, { Table } from 'dexie';

// Audio recording interface
export interface AudioRecording {
  id: string;
  blob: Blob;
  createdAt: number;
  duration: number;
  metadata?: {
    mimeType: string;
    size: number;
  };
  reportId?: number;
}

// Storage statistics interface
export interface StorageStats {
  count: number;
  recordings: AudioRecording[];
  totalSize: number;
}

const MAX_RECORDINGS = 3;

const DB_NAME = 'mohairnotes-audio';

/**
 * Database name used before the ConsultNow → MohairNotes rename.
 *
 * A recording lives ONLY here until the clinician hits Save/Submit, so renaming
 * the database without moving its contents would strand an in-progress consult
 * in an unreachable store. Retire this constant (and ``adoptLegacyRecordings``)
 * once chunked straight-to-S3 recording lands and the browser stops being the
 * only copy.
 */
const LEGACY_DB_NAME = 'consultnow-audio';

/**
 * Copy any recordings out of the pre-rename database, then drop it.
 *
 * Runs on every open — it is a cheap existence check for the overwhelming
 * majority of sessions, where no legacy database is present. Deliberately
 * total: a failure here logs and resolves, because a clinician with nothing to
 * migrate must never be locked out of the recorder by a migration that only
 * ever mattered to a handful of browser profiles.
 */
async function adoptLegacyRecordings(target: AudioDatabase): Promise<void> {
  try {
    if (!(await Dexie.exists(LEGACY_DB_NAME))) return;

    // Opened without a declared schema so Dexie adopts whatever version and
    // stores the old database actually has, rather than trying to upgrade it.
    const legacy = new Dexie(LEGACY_DB_NAME);
    let rows: AudioRecording[] = [];
    try {
      await legacy.open();
      const table = legacy.tables.find((t) => t.name === 'recordings');
      if (table) {
        rows = (await table.toArray()) as AudioRecording[];
      }
    } finally {
      legacy.close();
    }

    // bulkPut rather than bulkAdd: if a previous attempt copied some rows and
    // then failed before deleting the source, re-running must not throw on the
    // ids that already made it across.
    if (rows.length > 0) {
      await target.recordings.bulkPut(rows);
    }

    await Dexie.delete(LEGACY_DB_NAME);
  } catch (err) {
    console.warn('[audioDB] could not migrate recordings from the previous database', err);
  }
}

/**
 * Default staleness threshold for the cross-session sweep performed on
 * Quick Start / New Encounter / Encounter Detail page mount. Must be longer
 * than any realistic single recording session (consults run ~30–60 min) so
 * legitimate in-flight blobs are not destroyed, and far shorter than the
 * "next day" gap the field-reported leak spans.
 */
export const STALE_AUDIO_TTL_MS = 2 * 60 * 60 * 1000;

/**
 * Dexie-based AudioDatabase for storing audio recordings in IndexedDB
 * Replaces the raw IndexedDB implementation with a cleaner, promise-based API
 */
export class AudioDatabase extends Dexie {
  recordings!: Table<AudioRecording, string>;
  private channel: BroadcastChannel;

  constructor() {
    super(DB_NAME);

    // Define schema
    this.version(1).stores({
      recordings: 'id, createdAt, reportId',
    });

    // Adopt anything left in the pre-rename database before the first query is
    // served. Dexie holds every other query until this handler's promise
    // settles, so a page that mounts and immediately calls getAll() cannot
    // observe an empty store while the copy is still in flight.
    this.on('ready', () => adoptLegacyRecordings(this));

    // BroadcastChannel for cross-tab communication
    this.channel = new BroadcastChannel('audio-storage');
  }

  /**
   * Save recording with automatic cleanup if limit exceeded
   */
  async save(recording: Omit<AudioRecording, 'id' | 'createdAt'>): Promise<string> {
    // Check current count
    const count = await this.recordings.count();

    // If at limit, delete oldest recording
    if (count >= MAX_RECORDINGS) {
      const oldest = await this.recordings.orderBy('createdAt').first();
      if (oldest?.id) {
        await this.deleteRecording(oldest.id);
      }
    }

    const id = `recording-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const newRecording: AudioRecording = {
      ...recording,
      id,
      createdAt: Date.now(),
      metadata: {
        size: recording.blob.size,
        mimeType: recording.blob.type,
      },
    };

    await this.recordings.add(newRecording);

    // Notify other tabs
    this.channel.postMessage({
      type: 'recording-saved',
      id,
    });

    return id;
  }

  /**
   * Get recording by ID
   */
  async get(id: string): Promise<AudioRecording | undefined> {
    return this.recordings.get(id);
  }

  /**
   * Get all recordings, oldest first.
   *
   * Insertion-order ordering matters because the recovery effect on the
   * Quick Start / New Encounter pages numbers entries as "Recording N"
   * based on the position the merge helper sees. Without an explicit
   * ``orderBy``, Dexie returns rows in primary-key b-tree order, which
   * is not guaranteed chronological.
   */
  async getAll(): Promise<AudioRecording[]> {
    return this.recordings.orderBy('createdAt').toArray();
  }

  /**
   * Delete recording by ID
   */
  async deleteRecording(id: string): Promise<void> {
    await this.recordings.delete(id);

    // Notify other tabs
    this.channel.postMessage({
      type: 'recording-deleted',
      id,
    });
  }

  /**
   * Delete all recordings
   */
  async deleteAll(): Promise<void> {
    await this.recordings.clear();

    // Notify other tabs
    this.channel.postMessage({
      type: 'recordings-cleared',
    });
  }

  /**
   * Delete only recordings older than ``maxAgeMs`` milliseconds.
   *
   * Used by Quick Start / New Encounter / Encounter Detail pages on mount
   * to sweep stale blobs from prior un-submitted sessions WITHOUT touching
   * fresh blobs that may belong to:
   *   - the current tab's just-completed stopRecording (createdAt ≈ now,
   *     about to be picked up by the recovery effect), or
   *   - a parallel tab's active recording session.
   *
   * The TTL must be longer than any realistic single recording session
   * (so legitimate in-flight work survives) and shorter than the "next
   * day" gap the reported bug spans. Two hours hits that window.
   */
  async deleteStale(maxAgeMs: number): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const staleIds = await this.recordings.where('createdAt').below(cutoff).primaryKeys();
    if (staleIds.length === 0) return 0;
    await this.recordings.bulkDelete(staleIds);
    this.channel.postMessage({
      type: 'recordings-stale-pruned',
      count: staleIds.length,
    });
    return staleIds.length;
  }

  /**
   * Get storage statistics
   */
  async getStats(): Promise<StorageStats> {
    const recordings = await this.getAll();
    const totalSize = recordings.reduce((sum, rec) => sum + (rec.metadata?.size || 0), 0);

    return {
      totalSize,
      recordings,
      count: recordings.length,
    };
  }

  /**
   * Check if storage has recordings
   */
  async hasRecordings(): Promise<boolean> {
    const count = await this.recordings.count();
    return count > 0;
  }

  /**
   * Subscribe to storage changes from other tabs
   */
  onStorageChange(callback: (event: { type: string; id?: string }) => void): () => void {
    const handler = (event: MessageEvent) => {
      callback(event.data);
    };
    this.channel.addEventListener('message', handler);

    // Return unsubscribe function
    return () => {
      this.channel.removeEventListener('message', handler);
    };
  }

  /**
   * Close database and broadcast channel
   */
  closeAll(): void {
    this.close();
    this.channel.close();
  }
}

// Export singleton instance
export const audioDB = new AudioDatabase();

// Helper functions for backward compatibility
export const audioStorage = {
  save: (recording: Omit<AudioRecording, 'id' | 'createdAt'>) => audioDB.save(recording),
  get: (id: string) => audioDB.get(id),
  getAll: () => audioDB.getAll(),
  delete: (id: string) => audioDB.deleteRecording(id),
  deleteAll: () => audioDB.deleteAll(),
  getStats: () => audioDB.getStats(),
  hasRecordings: () => audioDB.hasRecordings(),
  onStorageChange: (callback: (event: { type: string; id?: string }) => void) => audioDB.onStorageChange(callback),
  close: () => audioDB.closeAll(),
};
